import { Subscribe } from '@react-rxjs/core'
import { useInterpret } from '@xstate/react'
import React, { FC, useEffect, useMemo } from 'react'
import { take } from 'rxjs'
import { DefaultContext, StateConfig } from 'xstate'

import {
  latestAuthStateWithAccessToken$,
  useAuthStateWithAccessToken,
} from '../../../../../lib/auth/state'
import { RewardsOnboardingServiceContext } from '../../../../../lib/rewards/onboarding/context'
import { rewardsOnboardingClaim } from '../../../../../lib/rewards/onboarding/state/claim'
import {
  machine,
  RewardsOnboardingEvent,
} from '../../../../../lib/rewards/onboarding/state/machine-rewards-onboarding'
import { persistRewardsOnboardingState } from '../../../../../lib/rewards/onboarding/state/persistence'
import { fromInterpreter } from '../../../../../utils/rx/interpreter'

const RewardsOnboardingServiceProviderContent: FC = ({ children }) => {
  const authState = useAuthStateWithAccessToken()
  const claim = useMemo(() => rewardsOnboardingClaim(authState), [authState])
  const persistRewardsOnboarding = useMemo(
    () => persistRewardsOnboardingState(authState),
    [authState],
  )

  const initialState: StateConfig<DefaultContext, RewardsOnboardingEvent> = useMemo(
    () => ({
      ...machine.initialState,
      value: claim?.state ?? 'pending',
      context: {},
    }),
    [claim?.state],
  )
  const rewardsOnboardingService = useInterpret(
    machine,
    {
      state: initialState,
    },
    persistRewardsOnboarding,
  )

  // send RESTORE event to trigger transition (if required) on restore
  useEffect(() => {
    const takeOne$ = fromInterpreter(rewardsOnboardingService).pipe(take(1))
    const subscription = takeOne$.subscribe((state) => {
      if (state.done) {
        return
      }

      rewardsOnboardingService.send({ type: 'RESTORE' })
    })
    return () => subscription.unsubscribe()
  }, [rewardsOnboardingService])

  return (
    <RewardsOnboardingServiceContext.Provider value={rewardsOnboardingService}>
      {children}
    </RewardsOnboardingServiceContext.Provider>
  )
}

export const RewardsOnboardingServiceProvider: FC = ({ children }) => {
  return (
    <Subscribe source$={latestAuthStateWithAccessToken$} fallback={null}>
      <RewardsOnboardingServiceProviderContent>{children}</RewardsOnboardingServiceProviderContent>
    </Subscribe>
  )
}
